import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import TrackRow from "@/components/music/TrackRow";
import { fmtTime } from "@/lib/youtube";
import { toast } from "sonner";
import { Search, Loader2, Play, Plus, Download, Music2, Disc3, Zap } from "lucide-react";

function CatalogSearch() {
  const [q, setQ] = useState("");
  const [tracks, setTracks] = useState([]);
  const run = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    try { const { data } = await api.get("/music/tracks", { params: { q } }); setTracks(data.tracks); }
    catch (err) { toast.error("Search failed"); }
  };
  return (
    <div className="p-4 md:p-8 pb-6">
      <h1 className="font-display text-3xl font-black tracking-tighter mb-4">Search</h1>
      <form onSubmit={run} className="pb-4">
        <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 max-w-xl focus-within:bg-white/15">
          <Search className="w-5 h-5 text-zinc-400" />
          <input data-testid="catalog-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the catalog..." className="flex-1 bg-transparent py-3 text-white placeholder-zinc-400 focus:outline-none" />
          <button data-testid="catalog-search-btn" type="submit" className="text-sm font-semibold text-white/80 hover:text-white">Search</button>
        </div>
      </form>
      <div>{tracks.map((t, i) => <TrackRow key={t.id} track={t} index={i} tracks={tracks} />)}</div>
      {q && tracks.length === 0 && <p className="text-zinc-500 text-sm py-8 text-center">No matches yet.</p>}
    </div>
  );
}

export default function MusicSearch() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [albumMode, setAlbumMode] = useState(false);
  const [albumName, setAlbumName] = useState("");
  const [importing, setImporting] = useState(false);
  const [bulkQuery, setBulkQuery] = useState("");
  const [bulkAlbum, setBulkAlbum] = useState(false);
  const [bulkAlbumName, setBulkAlbumName] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const { playQueue } = useMusicPlayer();

  const search = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setResults(null);
    setSelected({});
    try {
      const { data } = await api.post("/music/search", { query: q, limit: 20 });
      setResults(data.results);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Search error");
    } finally {
      setLoading(false);
    }
  };

  const importBulk = async () => {
    if (!bulkQuery.trim()) { toast.error("Enter an artist or album"); return; }
    setBulkLoading(true);
    try {
      const payload = { query: bulkQuery, limit: 25, as_album: bulkAlbum };
      if (bulkAlbum && bulkAlbumName.trim()) payload.album_name = bulkAlbumName.trim();
      const { data } = await api.post("/music/import-bulk", payload);
      toast.success(`${data.count} tema(s) importado(s) de ${data.found} encontrado(s)`);
      window.dispatchEvent(new Event("playlists-changed"));
      setBulkQuery("");
      setBulkAlbumName("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import error");
    } finally {
      setBulkLoading(false);
    }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const asTrack = (r) => ({ id: r.provider_id, provider_id: r.provider_id, title: r.title, artist: r.artist, duration: r.duration, thumbnail: r.thumbnail });

  const preview = (r, i) => playQueue(results.map(asTrack), i);

  const doImport = async (items) => {
    if (items.length === 0) { toast.error("Select at least one track"); return; }
    setImporting(true);
    try {
      const payload = { items };
      if (albumMode && albumName.trim()) {
        payload.album_name = albumName.trim();
        payload.album_artist = items[0].artist;
        payload.album_cover = items[0].thumbnail;
      }
      const { data } = await api.post("/music/import", payload);
      toast.success(`${data.count} tema(s) importado(s)`);
      window.dispatchEvent(new Event("playlists-changed"));
      setSelected({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import error");
    } finally {
      setImporting(false);
    }
  };

  const importSelected = () => doImport(results.filter((r) => selected[r.provider_id]).map(asTrack));
  const importAll = () => doImport(results.map(asTrack));

  const selectedCount = Object.values(selected).filter(Boolean).length;

  if (!isAdmin) return <CatalogSearch />;

  return (
    <div className="p-4 md:p-8 pb-6">
      <div className="bg-gradient-to-r from-[#FF2A4D]/15 to-purple-600/10 border border-white/10 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-[#FF2A4D]" />
          <h2 className="font-heading font-semibold">Importación rápida por artista o álbum</h2>
        </div>
        <p className="text-sm text-zinc-400 mb-3">Enter an artist or album y trae sus tracks al catálogo de una sola vez.</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="bulk-query-input"
            value={bulkQuery}
            onChange={(e) => setBulkQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && importBulk()}
            placeholder="Ej. Coldplay  ·  Daft Punk - Discovery"
            className="flex-1 min-w-[220px] bg-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A4D]"
          />
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={bulkAlbum} onChange={(e) => setBulkAlbum(e.target.checked)} data-testid="bulk-album-mode" className="accent-[#FF2A4D]" />
            <Disc3 className="w-4 h-4" /> As album
          </label>
          {bulkAlbum && (
            <input data-testid="bulk-album-name" value={bulkAlbumName} onChange={(e) => setBulkAlbumName(e.target.value)} placeholder="Album name (optional)" className="bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A4D]" />
          )}
          <button data-testid="bulk-import-btn" onClick={importBulk} disabled={bulkLoading} className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-lg px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
            {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> Import all</>}
          </button>
        </div>
      </div>

      <form onSubmit={search} className="pb-4">
        <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 max-w-xl focus-within:bg-white/15">
          <Search className="w-5 h-5 text-zinc-400" />
          <input
            data-testid="music-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="O busca tracks concretos en YouTube..."
            className="flex-1 bg-transparent py-3 text-white placeholder-zinc-400 focus:outline-none"
          />
          <button data-testid="music-search-btn" type="submit" className="text-sm font-semibold text-white/80 hover:text-white">Search</button>
        </div>
      </form>

      {loading && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>}

      {results && (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <button data-testid="import-all-btn" onClick={importAll} disabled={importing} className="bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-5 py-2 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Import all ({results.length})
            </button>
            <button data-testid="import-selected-btn" onClick={importSelected} disabled={importing || selectedCount === 0} className="bg-white/10 hover:bg-white/20 rounded-full px-5 py-2 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-40">
              <Plus className="w-4 h-4" /> Import selected ({selectedCount})
            </button>
            <label className="flex items-center gap-2 text-sm text-zinc-300 ml-2">
              <input type="checkbox" checked={albumMode} onChange={(e) => setAlbumMode(e.target.checked)} data-testid="album-mode" className="accent-[#FF2A4D]" />
              <Disc3 className="w-4 h-4" /> As album
            </label>
            {albumMode && (
              <input data-testid="album-name-input" value={albumName} onChange={(e) => setAlbumName(e.target.value)} placeholder="Name del álbum" className="bg-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A4D]" />
            )}
          </div>

          <div className="space-y-1">
            {results.map((r, i) => (
              <div key={r.provider_id} data-testid={`search-result-${r.provider_id}`} className="group flex items-center gap-3 px-3 py-2 rounded-md hover:bg-white/5">
                <input type="checkbox" checked={!!selected[r.provider_id]} onChange={() => toggle(r.provider_id)} data-testid={`select-${r.provider_id}`} className="accent-[#FF2A4D] w-4 h-4" />
                <div className="relative shrink-0">
                  {r.thumbnail ? <img src={r.thumbnail} alt="" className="w-11 h-11 rounded object-cover" /> : <div className="w-11 h-11 rounded bg-white/10 flex items-center justify-center"><Music2 className="w-4 h-4 text-zinc-500" /></div>}
                  <button onClick={() => preview(r, i)} data-testid={`preview-${r.provider_id}`} className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded transition-opacity">
                    <Play className="w-4 h-4 text-white" fill="white" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.title}</p>
                  <p className="text-xs text-zinc-400 truncate">{r.artist}</p>
                </div>
                <span className="text-xs text-zinc-500 font-mono">{fmtTime(r.duration)}</span>
                <button onClick={() => doImport([asTrack(r)])} data-testid={`import-one-${r.provider_id}`} className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10" title="Importar">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {results === null && !loading && (
        <div className="text-center py-24 text-zinc-500">
          <Search className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Search and add music to the catalog.</p>
        </div>
      )}
    </div>
  );
}
